import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "../components/Skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  assignDeliveryDriver,
  cancelQuotation,
  deleteKupatHolimRequest,
  getAddressById,
  getKupatHolimRequest,
  getOrder,
  listDashboardWorkQueue,
  listDeliveryDrivers,
  reviewKupatHolimRequest,
  updateOrderDeliveryDate,
} from "../services/adminDataService";
import OrderDetailModal from "../modal/OrderDetailModal";
import DeliveryLabelModal from "../modal/DeliveryLabelModal";
import KupatHolimReviewModal from "../modal/KupatHolimReviewModal";
import { confirmAction } from "../components/ConfirmationModal";
import QuoteModal from "../modal/RenewalModal";
import {
  cancelOrderCached,
  deleteOrderCached,
  fetchOrders,
  fetchUsers,
  respondToQuotationCached,
} from "../store/adminDataSlice";

// Retained for the dashboard's legacy detail actions outside the work-queue table.
// eslint-disable-next-line no-unused-vars
const orderStatuses = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
// eslint-disable-next-line no-unused-vars
const quotationStatuses = ["requested", "pending", "responded", "cancelled"];
const dashboardPageSize = 5;
const dashboardRefreshInterval = 30000;
const invoiceFollowUpDelayMs = 30 * 60 * 1000;
const defaultMapCenter = [31.7683, 35.2137];
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let googleMapsLoader;

const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoader) return googleMapsLoader;
  if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error("Google Maps API key is not configured."));

  googleMapsLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&v=weekly`;
    script.async = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Unable to load Google Maps."));
    document.head.appendChild(script);
  });
  return googleMapsLoader;
};
const fallbackDeliveryCenters = {
  threeHour: { latitude: 31.7703, longitude: 35.2097 },
  sameDay: { latitude: 31.7663, longitude: 35.2177 },
  followingDays: { latitude: 31.7742, longitude: 35.2205 },
};
const deliveryOrderTypes = {
  threeHour: {
    label: "3 Hours Delivery Orders",
    icon: "/assets/img/pin_red.svg",
  },
  sameDay: {
    label: "Same Day Delivery Orders",
    icon: "/assets/img/pin_blue.svg",
  },
  followingDays: {
    label: "Other Days Delivery Orders",
    icon: "/assets/img/pin_yellow.svg",
  },
};

const getUserLabel = (order) => {
  const user = order.user || order.customer || order.client || order.patient || {};
  const fullName =
    user.name ||
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    order.clientName ||
    order.customerName ||
    order.userName ||
    order.patientName ||
    order.fullName ||
    [order.firstName, order.lastName].filter(Boolean).join(" ");

  return (
    fullName ||
    user.email ||
    order.clientEmail ||
    order.customerEmail ||
    order.userEmail ||
    `User #${order.userId || order.clientId || order.customerId || "-"}`
  );
};

const getOrderStatus = (order) => {
  const status = order?.status || order?.orderStatus || order?.order_status || "pending";
  return status?.value || status?.sourceStatus || status;
};

const getOrderAddressId = (order) =>
  order?.addressId ||
  order?.address_id ||
  order?.deliveryAddressId ||
  order?.delivery_address_id ||
  order?.shippingAddressId ||
  order?.shipping_address_id;

const getNormalizedStatus = (status, fallback = "pending") =>
  String(status || fallback).toLowerCase();

// The dashboard uses a concise operational status set. Prescription-review
// outcomes that still require work are represented as Order in Progress.
const getDashboardStatusKey = (status) => {
  const normalizedStatus = getNormalizedStatus(status);

  if ([
    "pending",
    "in_progress",
    "order_in_progress",
    "failed",
    "approved",
    "approved_no_prescription",
    "no_prescription",
    "rejected",
  ].includes(normalizedStatus)) {
    return "in_progress";
  }
  if (normalizedStatus === "approved_prescription_found") {
    return "in_progress";
  }
  if (normalizedStatus === "to_prepare") {
    return "to_be_prepared";
  }
  if (["to_be_invoiced", "to_invoice"].includes(normalizedStatus)) return "to_be_invoiced";
  if (normalizedStatus === "invoiced") return "invoiced";
  if (normalizedStatus === "prepared") return "prepared";
  if (normalizedStatus === "shipped") return "shipped";
  if (normalizedStatus === "delivered") return "delivered";

  return null;
};

const dashboardStatusLabels = {
  in_progress: "Order in Progress",
  to_be_invoiced: "To Be Invoiced",
  invoiced: "Invoiced",
  to_be_prepared: "To Be Prepared",
  prepared: "Prepared",
  shipped: "Shipped",
  delivered: "Delivered",
};

const getNextDashboardStatus = (status) => ({
  to_be_prepared: "prepared",
  prepared: "shipped",
  shipped: "delivered",
}[getDashboardStatusKey(status)] || null);

const getOrderDashboardStatus = (order) =>
  getDashboardStatusKey(order.dashboardStatus || order.statusValue || order.status) || order.dashboardStatus || null;

const formatStatusLabel = (status) =>
  getNormalizedStatus(status)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatOrderStatusLabel = (status) => {
  const normalizedStatus = getNormalizedStatus(status);

  if (normalizedStatus === "pending") return "Order Placed";

  return formatStatusLabel(normalizedStatus);
};

const hasQuoteBeenResponded = (item) => {
  const status = getNormalizedStatus(item.status, "requested");

  return ["responded", "closed", "cancelled"].includes(status);
};

const getDashboardRowTypeLabel = (row) =>
  row.type === "quotation_request" || row.rowType === "quotation"
    ? "Quote Request"
    : "Order";

const isDashboardQuotationRequest = (row) =>
  row.type === "quotation_request" || row.rowType === "quotation";

// eslint-disable-next-line no-unused-vars
const isClosedDashboardQuotationRequest = (row) =>
  isDashboardQuotationRequest(row) && getNormalizedStatus(row.status) === "closed";

const getDashboardClientName = (row) => {
  const client = row.client || {};
  const user = row.user || {};
  const customer = row.customer || {};
  const name =
    client.name ||
    user.name ||
    customer.name ||
    client.fullName ||
    user.fullName ||
    customer.fullName ||
    row.clientName ||
    row.userName ||
    row.customerName ||
    [client.firstName, client.lastName].filter(Boolean).join(" ") ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    [customer.firstName, customer.lastName].filter(Boolean).join(" ");

  return name || "-";
};

const normalizeDashboardRequestOrderRow = (row) => {
  const isKupatRequest = row.type === "kupat_holim_request";
  const isQuoteRequest = row.type === "quotation_request";
  const rawStatusValue =
    row.status?.value || row.statusValue || row.status || (isQuoteRequest ? "requested" : "pending");
  const statusValue = getNormalizedStatus(rawStatusValue, isQuoteRequest ? "requested" : "pending");
  const client = row.client || row.customer || row.user || {};

  return {
    ...row,
    rowId: `${row.type || (isQuoteRequest ? "quotation_request" : "order")}-${row.id}`,
    rowType: isKupatRequest ? "kupat_holim_request" : isQuoteRequest ? "quotation" : "order",
    quotationId: isQuoteRequest ? row.id : row.quotationId,
    referenceNo: row.referenceNo || (isQuoteRequest ? `Quote #${row.id}` : `#${row.id}`),
    user: row.patient || row.user || client,
    client,
    customer: row.customer || client,
    status: row.status?.sourceStatus || statusValue,
    statusLabel: row.status?.label || formatStatusLabel(statusValue),
    statusValue,
    dashboardStatus: getDashboardStatusKey(statusValue),
    statusGroup: row.status?.group || (isKupatRequest ? "kupat" : isQuoteRequest ? "quotation" : "order"),
    totalAmount: row.total,
    actions: row.action || row.actions || {},
  };
};

const linkedOrderId = (row) =>
  row.orderId || row.order_id || row.linkedOrderId || row.linked_order_id ||
  row.order?.id || row.order?._id || row.sourceOrder?.id || row.sourceOrder?._id;

const getAddressLabel = (order) => {
  const address =
    order.address ||
    order.deliveryAddress ||
    order.shippingAddress ||
    order.user?.address ||
    {};

  return (
    address.fullAddress ||
    address.address ||
    address.street ||
    address.city ||
    order.addressText ||
    order.deliveryAddressText ||
    "-"
  );
};

const DashboardAddressCell = ({ order }) => {
  const [expanded, setExpanded] = useState(false);
  const address = getAddressLabel(order);
  const canExpand = address.length > 70;

  return <div className={`dashboard-address-preview${expanded ? " is-expanded" : ""}`}>
    <span title={address}>{address}</span>
    {canExpand && <button type="button" onClick={() => setExpanded((current) => !current)} aria-expanded={expanded}>{expanded ? "Less" : "More"}</button>}
  </div>;
};

const getAddressSearchText = (address) => {
  if (!address) return "";

  return [
    address.fullAddress,
    address.address,
    address.addressLine,
    address.street,
    address.city,
    address.state,
    address.pincode,
    address.country || "Israel",
  ]
    .filter(Boolean)
    .join(", ");
};

const getOrderAddressSearchText = (order) => {
  const addressCandidates = [
    order.address,
    order.deliveryAddress,
    order.shippingAddress,
    order.user?.address,
  ];

  for (const address of addressCandidates) {
    const searchText = getAddressSearchText(address);

    if (searchText) return searchText;
  }

  return [
    order.fullAddress,
    order.addressText,
    order.deliveryAddressText,
    order.shippingAddressText,
    order.street,
    order.city,
    order.state,
    order.pincode,
    order.country || "Israel",
  ]
    .filter(Boolean)
    .join(", ");
};

const geocodeAddress = async (searchText) => {
  if (!searchText) return null;

  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(searchText)}`,
  );
  const results = await response.json();
  const result = results?.[0];

  if (!result?.lat || !result?.lon) return null;

  return {
    latitude: Number(result.lat),
    longitude: Number(result.lon),
  };
};

const getCoordinateKey = (coordinates) =>
  `${Number(coordinates.latitude).toFixed(5)},${Number(coordinates.longitude).toFixed(5)}`;

const getOffsetPosition = (coordinates, index, total) => {
  if (total <= 1) return [coordinates.latitude, coordinates.longitude];

  const angle = (2 * Math.PI * index) / total;
  const radius = 0.00018 + Math.floor(index / 8) * 0.00008;

  return [
    coordinates.latitude + Math.sin(angle) * radius,
    coordinates.longitude + Math.cos(angle) * radius,
  ];
};

const getCoordinatePair = (value) => {
  if (!value) return null;

  if (Array.isArray(value) && value.length >= 2) {
    return {
      latitude: value[1],
      longitude: value[0],
    };
  }

  const coordinates = value.coordinates || value.coords;

  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    return {
      latitude: coordinates[1],
      longitude: coordinates[0],
    };
  }

  return {
    latitude:
      value.latitude ??
      value.lat ??
      value.deliveryLatitude ??
      value.deliveryLat ??
      value.addressLatitude ??
      value.addressLat,
    longitude:
      value.longitude ??
      value.lng ??
      value.lon ??
      value.deliveryLongitude ??
      value.deliveryLng ??
      value.addressLongitude ??
      value.addressLng,
  };
};

const getOrderCoordinates = (order) => {
  const candidates = [
    order,
    order.location,
    order.geoLocation,
    order.deliveryLocation,
    order.address,
    order.address?.location,
    order.address?.geoLocation,
    order.deliveryAddress,
    order.deliveryAddress?.location,
    order.deliveryAddress?.geoLocation,
    order.shippingAddress,
    order.shippingAddress?.location,
    order.shippingAddress?.geoLocation,
    order.user?.address,
    order.user?.location,
  ];

  for (const candidate of candidates) {
    const coordinatePair = getCoordinatePair(candidate);
    const parsedLatitude = Number(coordinatePair?.latitude);
    const parsedLongitude = Number(coordinatePair?.longitude);

    if (!Number.isNaN(parsedLatitude) && !Number.isNaN(parsedLongitude)) {
      return {
        latitude: parsedLatitude,
        longitude: parsedLongitude,
      };
    }
  }

  return null;
};

const getDeliveryMapType = (order) => {
  const deliveryType = Number(order.deliveryType);
  if (deliveryType === 1) return "threeHour";
  if (deliveryType >= 2) return "followingDays";
  return "sameDay";
};

const isMapVisibleOrder = (order) => getNormalizedStatus(getOrderStatus(order)) !== "delivered";

const getOrderTotal = (order) => {
  return order.totalAmount || order.total || order.grandTotal || order.amount || 0;
};

const getDashboardRowDateValue = (order) =>
  order.dateTime ||
  order.deliveryDate ||
  order.createdAt ||
  order.created_at ||
  order.updatedAt ||
  order.updated_at;

const getDashboardRowTimestamp = (order) => {
  const timestamp = new Date(getDashboardRowDateValue(order)).getTime();
  return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
};

const isDashboardRowToday = (order) => {
  const date = new Date(getDashboardRowDateValue(order));
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  return date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
};

const formatOrderDate = (order) => {
  const dateValue = getDashboardRowDateValue(order);

  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatOrderTime = (order) => {
  const dateValue = getDashboardRowDateValue(order);
  if (!dateValue) return "--:--";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

// eslint-disable-next-line no-unused-vars
const getStatusClass = (status) => {
  const dashboardStatus = getDashboardStatusKey(status);

  if (dashboardStatus === "in_progress") {
    return "dropdown_requested";
  }
  if (["to_be_invoiced", "invoiced"].includes(dashboardStatus)) return "dropdown_to_be_invoiced";
  if (dashboardStatus === "prepared") {
    return "dropdown_prepared";
  }
  if (dashboardStatus === "to_be_prepared") return "dropdown_to_be_prepared";
  if (dashboardStatus === "shipped") return "dropdown_out_delivery";
  if (dashboardStatus === "delivered") return "dropdown_delivered";

  return "dropdown_order_ready";
};

const getDashboardStatusLabel = (order) => {
  const dashboardStatus = getOrderDashboardStatus(order);
  return dashboardStatusLabels[dashboardStatus] || order.statusLabel;
};

const getStatusUpdatedAt = (order) =>
  order?.statusUpdatedAt ||
  order?.status_updated_at ||
  order?.statusChangedAt ||
  order?.status_changed_at ||
  order?.toBeInvoicedAt ||
  order?.to_be_invoiced_at ||
  order?.status?.updatedAt ||
  order?.status?.updated_at ||
  order?.updatedAt ||
  order?.updated_at;

const requiresInvoiceFollowUp = (order) => {
  if (getOrderDashboardStatus(order) !== "to_be_invoiced") return false;

  const updatedAt = new Date(getStatusUpdatedAt(order)).getTime();
  return !Number.isNaN(updatedAt) && Date.now() - updatedAt >= invoiceFollowUpDelayMs;
};

const DashboardStatusControl = ({ order, onAdvance }) => {
  const nextStatus = getNextDashboardStatus(order.statusValue || order.status);
  const currentLabel = getDashboardStatusLabel(order);
  const nextLabel = dashboardStatusLabels[nextStatus];
  const showInvoiceFollowUp = requiresInvoiceFollowUp(order);

  return <div className={`${getStatusClass(order.statusValue || order.status)} dashboard-status-wrapper`}>
    {showInvoiceFollowUp && <span className="dashboard-invoice-follow-up" role="img" aria-label="Invoice follow-up required" title="Patient has not completed invoicing after 30 minutes">
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
    </span>}
    <div className="dashboard-status-control">
      {getOrderDashboardStatus(order) !== "in_progress" && <span className="dashboard-status-previous" aria-hidden="true">‹</span>}
      <span className="dashboard-status-label">{currentLabel}</span>
      {nextStatus && <button
        type="button"
        className="dashboard-status-next"
        onClick={() => onAdvance(order, nextStatus)}
        aria-label={`Move order from ${currentLabel} to ${nextLabel}`}
        title={`Move to ${nextLabel}`}
      >
        <span aria-hidden="true">›</span>
      </button>}
    </div>
  </div>;
};

const getAssignedDriverId = (order) => {
  const driver = order?.driver || order?.deliveryDriver || order?.assignedDriver || order?.assignedTo || order?.driverDetails || order?.delivery?.driver || {};
  const activityActor = order?.activity?.actor || {};
  return order?.driverId || order?.driver_id || order?.driverUserId || order?.deliveryDriverId || order?.delivery_driver_id || order?.deliveryAgentId || order?.delivery_agent_id || order?.assignedDriverId || order?.assigned_driver_id || order?.assignedToId || order?.assigned_to_id || driver.id || driver._id || driver.userId || driver.driverId || activityActor.id || activityActor._id || "";
};

const getDriverLabel = (driver) => driver?.name || driver?.fullName || driver?.email || `Driver #${driver?.id || driver?._id}`;

const getDriverFirstWord = (driver) => String(getDriverLabel(driver)).trim().split(/\s+/)[0] || "";

const getDashboardActivityTime = (order) => {
  const occurredAt = order.activity?.occurredAt;
  if (!occurredAt) return "";

  const timestamp = new Date(occurredAt);
  if (Number.isNaN(timestamp.getTime())) return "";
  return timestamp.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const getDashboardActivityActor = (order) => {
  if ((order.dashboardStatus || getDashboardStatusKey(order.statusValue || order.status)) === "in_progress") return "";

  const actor = order.activity?.actor || {};
  const actorName = actor.firstName || actor.first_name || actor.givenName || actor.name || actor.fullName || actor.full_name || actor.user?.name || order.activity?.actorName || "";
  return String(actorName).trim().split(/\s+/)[0] || "";
};

const toDateTimeLocal = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const RescheduleDeliveryModal = ({ order, onClose, onSaved }) => {
  const [deliveryDate, setDeliveryDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!order?.id) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    getOrder(order.id).then((detail) => {
      if (!active) return;
      setDeliveryDate(toDateTimeLocal(getDashboardRowDateValue(detail)));
    }).catch((loadError) => {
      if (active) setError(loadError.message || "Unable to load the delivery date.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [order]);

  if (!order) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!deliveryDate) {
      setError("Choose a new delivery date and time.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await updateOrderDeliveryDate(order.id, new Date(deliveryDate).toISOString());
      await onSaved();
      onClose();
    } catch (saveError) {
      setError(saveError.message || "Unable to reschedule the delivery.");
    } finally {
      setSaving(false);
    }
  };

  return <><div className="modal show" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="reschedule-delivery-title"><div className="modal-dialog modal-dialog-centered"><form className="modal-content" onSubmit={submit}><div className="modal-header"><div><h5 className="modal-title" id="reschedule-delivery-title">Reschedule delivery</h5><p className="small text-muted mb-0">Order #{order.id}</p></div><button type="button" className="btn-close" onClick={onClose} disabled={saving} aria-label="Close" /></div><div className="modal-body">{loading ? <Skeleton width="100%" height="42px" /> : <><label className="form-label" htmlFor="reschedule-delivery-date">New delivery date and time</label><input id="reschedule-delivery-date" type="datetime-local" className="form-control" value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} disabled={saving} />{error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}</>}</div><div className="modal-footer"><button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading || saving}>{saving ? "Saving…" : "Save delivery date"}</button></div></form></div></div><div className="modal-backdrop fade show" /></>;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedOrderRef = useRef("");
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState("");
  const {
    orders: orderResource,
    users: userResource,
  } = useSelector((state) => state.adminData);
  const orders = orderResource.items;
  const navigate = useNavigate();
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [, setError] = useState("");
  const [, setMessage] = useState("");
  const [quoteError, setQuoteError] = useState("");
  const [quoteOrder, setQuoteOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailView, setDetailView] = useState("delivery");
  const [requestedStatusAction, setRequestedStatusAction] = useState(null);
  const [deliveryLabelOrder, setDeliveryLabelOrder] = useState(null);
  const [rescheduleOrder, setRescheduleOrder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [addressLocations, setAddressLocations] = useState({});
  const [orderLocations, setOrderLocations] = useState({});
  const [dashboardPage, setDashboardPage] = useState(1);
  const [dashboardSearch, setDashboardSearch] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driverSavingOrderId, setDriverSavingOrderId] = useState("");
  const [driverAssignmentError, setDriverAssignmentError] = useState({ orderId: "", message: "" });
  const [kupatReviewSavingId, setKupatReviewSavingId] = useState("");
  const [kupatReviewRequest, setKupatReviewRequest] = useState(null);
  const [kupatReviewLoading, setKupatReviewLoading] = useState(false);
  const [kupatReviewError, setKupatReviewError] = useState("");
  const [kupatReviewSaving, setKupatReviewSaving] = useState(false);
  const dashboardRequestRef = useRef(0);
  const [dashboardList, setDashboardList] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: dashboardPageSize,
    totalPages: 1,
    status: "idle",
    error: "",
  });
  const dashboardLoading = dashboardList.status === "loading";

  const dashboardRows = dashboardList.items;

  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const todaysQuoteRequests = dashboardRows.filter((row) =>
      isDashboardQuotationRequest(row) &&
      new Date(row.createdAt || row.updatedAt || 0).toDateString() === today,
    ).length;
    const deliveredSales = orders
      .filter((order) => order.status === "delivered")
      .reduce((sum, order) => sum + Number(getOrderTotal(order) || 0), 0);

    return {
      totalUsers: userResource.items.length,
      todaysQuoteRequests,
      totalOrders: orders.length,
      deliveredSales,
    };
  }, [dashboardRows, orders, userResource.items]);
  const dashboardTotalPages = Math.max(1, dashboardList.totalPages);
  const currentDashboardPage = Math.min(dashboardPage, dashboardTotalPages);
  const dashboardPageStart = (currentDashboardPage - 1) * dashboardPageSize;
  const paginatedDashboardRows = todayOnly
    ? dashboardRows.filter(isDashboardRowToday)
    : dashboardRows;
  const firstVisibleRow = dashboardList.total === 0 ? 0 : dashboardPageStart + 1;
  const lastVisibleRow = Math.min(
    dashboardPageStart + dashboardPageSize,
    dashboardList.total,
  );

  // Use the complete orders response so every successful order is represented on the map.
  // Delivered orders are intentionally excluded.
  const activeDeliveryOrders = useMemo(
    () => orders.filter(isMapVisibleOrder),
    [orders],
  );

  const deliveryMapOrders = useMemo(() => {
    return activeDeliveryOrders
      .map((order) => {
        const addressId = getOrderAddressId(order);
        const addressLocation = addressId ? addressLocations[addressId] : null;
        const orderWithAddress = {
          ...order,
          address: order.address || addressLocation?.address,
        };
        const coordinates =
          getOrderCoordinates(orderWithAddress) ||
          addressLocation?.coordinates ||
          orderLocations[order.id]?.coordinates;

        const type = getDeliveryMapType(order);
        const hasExactLocation = Boolean(coordinates);
        const fallbackCoordinates = fallbackDeliveryCenters[type];
        const markerCoordinates = coordinates || fallbackCoordinates;

        return {
          id: order.id,
          type,
          customer: getUserLabel(orderWithAddress),
          area: getAddressLabel(orderWithAddress),
          status: order.status,
          deliveryType: Number(order.deliveryType),
          hasExactLocation,
          latitude: markerCoordinates.latitude,
          longitude: markerCoordinates.longitude,
        };
      })
      .filter(Boolean);
  }, [activeDeliveryOrders, addressLocations, orderLocations]);

  const deliveryOrderNotations = useMemo(
    () =>
      Object.entries(deliveryOrderTypes).map(([type, config]) => ({
        ...config,
        type,
        legendLabel: type === "threeHour" ? "Urgent" : type === "sameDay" ? "Same day" : "Other days",
        count: activeDeliveryOrders.filter(
          (order) => getDeliveryMapType(order) === type,
        ).length,
      })),
    [activeDeliveryOrders],
  );

  const loadDashboard = useCallback((force = false) => {
    setError("");
    return Promise.all(
      [fetchOrders({ force }), fetchUsers({ force })].map(
        (request) => {
          const result = dispatch(request);

          return force ? result.unwrap() : result;
        },
      ),
    );
  }, [dispatch]);

  const loadDashboardRequestsOrders = useCallback(async ({ silent = false } = {}) => {
    const requestId = dashboardRequestRef.current + 1;
    dashboardRequestRef.current = requestId;

    if (!silent) {
      setDashboardList((previous) => ({
        ...previous,
        status: "loading",
        error: "",
      }));
    }

    try {
      const response = await listDashboardWorkQueue({
        page: dashboardPage,
        limit: dashboardPageSize,
        type: "all",
        search: dashboardSearch,
      });
      if (requestId !== dashboardRequestRef.current) return;

      const totalPages = Math.max(
        1,
        Math.ceil(response.total / (response.limit || dashboardPageSize)),
      );

      // A refresh can reduce the final page's item count. Load the last valid
      // page instead of leaving the table empty with disabled pagination.
      if (dashboardPage > totalPages) {
        setDashboardPage(totalPages);
        return;
      }

      const dashboardItems = response.items
        .map(normalizeDashboardRequestOrderRow)
        .sort((first, second) => getDashboardRowTimestamp(first) - getDashboardRowTimestamp(second));

      setDashboardList({
        ...response,
        totalPages,
        items: dashboardItems,
        status: "succeeded",
        error: "",
      });
    } catch (listError) {
      if (requestId !== dashboardRequestRef.current) return;
      if (silent) return;

      setDashboardList((previous) => ({
        ...previous,
        status: "failed",
        error: listError.message || "Unable to load the dashboard work queue.",
      }));
    }
  }, [dashboardPage, dashboardSearch]);

  const refreshOrderAndQuoteData = useCallback(() => {
    return Promise.all([
      dispatch(fetchOrders({ force: true })).unwrap(),
      loadDashboardRequestsOrders(),
    ]);
  }, [dispatch, loadDashboardRequestsOrders]);

  const reassignDriver = async (order, driverId) => {
    if (!driverId || String(driverId) === String(getAssignedDriverId(order))) return;

    try {
      setDriverSavingOrderId(String(order.id));
      setDriverAssignmentError({ orderId: "", message: "" });
      await assignDeliveryDriver(order.id, Number(driverId));
      await refreshOrderAndQuoteData();
    } catch (assignmentError) {
      setDriverAssignmentError({ orderId: String(order.id), message: assignmentError.message || "Unable to update the assigned driver." });
    } finally {
      setDriverSavingOrderId("");
    }
  };

  const reviewKupatRequestFromDashboard = async (request, status) => {
    try {
      setKupatReviewSavingId(String(request.id));
      setError("");
      await reviewKupatHolimRequest(request.id, { status });
      await refreshOrderAndQuoteData();
    } catch (reviewError) {
      setError(reviewError.message || "Unable to update the Kupat Holim request.");
    } finally {
      setKupatReviewSavingId("");
    }
  };

  const openKupatReviewFromDashboard = async (request) => {
    setKupatReviewRequest(null);
    setKupatReviewError("");
    setKupatReviewLoading(true);
    try {
      setKupatReviewRequest(await getKupatHolimRequest(request.id));
    } catch (loadError) {
      setKupatReviewError(loadError.message || "Unable to load the Kupat Holim request.");
    } finally {
      setKupatReviewLoading(false);
    }
  };

  const closeKupatReviewFromDashboard = () => {
    if (kupatReviewSaving) return;
    setKupatReviewRequest(null);
    setKupatReviewError("");
  };

  const submitKupatReviewFromDashboard = async (id, payload, setFormError) => {
    try {
      setKupatReviewSaving(true);
      await reviewKupatHolimRequest(id, payload);
      setKupatReviewRequest(null);
      await refreshOrderAndQuoteData();
    } catch (reviewError) {
      setFormError(reviewError.message || "Unable to submit the review.");
    } finally {
      setKupatReviewSaving(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    let active = true;
    listDeliveryDrivers().then((records) => {
      if (active) setDrivers(records);
    }).catch(() => {
      if (active) setDrivers([]);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const requestedOrderId = searchParams.get("order");
    if (!requestedOrderId || openedOrderRef.current === requestedOrderId) return;

    openedOrderRef.current = requestedOrderId;
    const cachedOrder = orders.find(
      (order) => String(order.id ?? order._id ?? order.orderId) === requestedOrderId,
    );

    const openOrder = async () => {
      try {
        const order = cachedOrder || await getOrder(requestedOrderId);
        setDetailView("delivery");
        setDetailOrder(order);
      } catch (loadError) {
        setError(loadError.message || "Unable to load this order.");
      } finally {
        setSearchParams({}, { replace: true });
      }
    };

    openOrder();
  }, [orders, searchParams, setSearchParams]);

  useEffect(() => {
    loadDashboardRequestsOrders();
  }, [loadDashboardRequestsOrders]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        loadDashboardRequestsOrders({ silent: true });
      }
    };

    const intervalId = window.setInterval(refreshWhenVisible, dashboardRefreshInterval);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [loadDashboardRequestsOrders]);

  useEffect(() => {
    const handlePageSearch = (event) => {
      if (event.detail?.path === "/dashboard") {
        setDashboardPage(1);
        setDashboardSearch(event.detail.query || "");
      }
    };

    window.addEventListener("admin-page-search", handlePageSearch);
    return () => window.removeEventListener("admin-page-search", handlePageSearch);
  }, []);

  useEffect(() => {
    const loadMissingAddressLocations = async () => {
      const missingAddressIds = [
        ...new Set(
          activeDeliveryOrders
            .map((order) => getOrderAddressId(order))
            .filter((addressId) => addressId && !addressLocations[addressId]),
        ),
      ];

      if (missingAddressIds.length === 0) return;

      const nextLocations = {};

      await Promise.all(
        missingAddressIds.map(async (addressId) => {
          try {
            const address = await getAddressById(addressId);
            const directCoordinates = getOrderCoordinates({ address });
            let coordinates = directCoordinates;

            if (!coordinates) {
              const searchText = getAddressSearchText(address);

              if (searchText) {
                coordinates = await geocodeAddress(searchText);
              }
            }

            nextLocations[addressId] = {
              address,
              coordinates,
            };
          } catch (addressError) {
            nextLocations[addressId] = {
              address: null,
              coordinates: null,
            };
          }
        }),
      );

      setAddressLocations((previous) => ({
        ...previous,
        ...nextLocations,
      }));
    };

    loadMissingAddressLocations();
  }, [activeDeliveryOrders, addressLocations]);

  useEffect(() => {
    const loadMissingOrderLocations = async () => {
      const missingOrders = activeDeliveryOrders.filter((order) => {
        if (orderLocations[order.id]) return false;

        const addressId = getOrderAddressId(order);
        const addressLocation = addressId ? addressLocations[addressId] : null;

        if (addressId && !addressLocation) return false;

        const orderWithAddress = {
          ...order,
          address: order.address || addressLocation?.address,
        };
        const coordinates =
          getOrderCoordinates(orderWithAddress) || addressLocation?.coordinates;

        return !coordinates && Boolean(getOrderAddressSearchText(orderWithAddress));
      });

      if (missingOrders.length === 0) return;

      const nextLocations = {};
      const coordinatesBySearchText = {};

      await Promise.all(
        [...new Set(
          missingOrders
            .map((order) => {
              const addressId = getOrderAddressId(order);
              const addressLocation = addressId ? addressLocations[addressId] : null;
              const orderWithAddress = {
                ...order,
                address: order.address || addressLocation?.address,
              };

              return getOrderAddressSearchText(orderWithAddress);
            })
            .filter(Boolean),
        )].map(async (searchText) => {
          try {
            coordinatesBySearchText[searchText] = await geocodeAddress(searchText);
          } catch (locationError) {
            coordinatesBySearchText[searchText] = null;
          }
        }),
      );

      missingOrders.forEach((order) => {
          const addressId = getOrderAddressId(order);
          const addressLocation = addressId ? addressLocations[addressId] : null;
          const orderWithAddress = {
            ...order,
            address: order.address || addressLocation?.address,
          };
        const searchText = getOrderAddressSearchText(orderWithAddress);

        nextLocations[order.id] = {
          coordinates: coordinatesBySearchText[searchText] || null,
        };
      });

      setOrderLocations((previous) => ({
        ...previous,
        ...nextLocations,
      }));
    };

    loadMissingOrderLocations();
  }, [activeDeliveryOrders, addressLocations, orderLocations]);

  useEffect(() => {
    let active = true;
    const handleGoogleMapsAuthFailure = () => {
      if (active) {
        setMapError("Google Maps is blocked for this API key. Enable Maps JavaScript API and allow this site in the key's HTTP referrers.");
        setMapReady(false);
      }
    };

    // Google reports key/API restriction failures through this global callback
    // after the script itself has loaded successfully.
    window.gm_authFailure = handleGoogleMapsAuthFailure;

    loadGoogleMaps().then(() => { if (active) setMapReady(true); }).catch((loadError) => {
      if (active) setMapError(loadError.message || "Unable to load the delivery map.");
    });
    return () => {
      active = false;
      if (window.gm_authFailure === handleGoogleMapsAuthFailure) delete window.gm_authFailure;
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return undefined;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: defaultMapCenter[0], lng: defaultMapCenter[1] },
      zoom: 12,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "cooperative",
      styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }, { featureType: "transit", stylers: [{ visibility: "off" }] }],
    });
    return () => {
      markerRefs.current.forEach((marker) => marker.setMap(null));
      markerRefs.current = [];
      mapInstanceRef.current = null;
    };
  }, [mapReady]);

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !window.google?.maps) return;

    markerRefs.current.forEach((marker) => marker.setMap(null));
    markerRefs.current = [];

    const bounds = [];
    const coordinateTotals = deliveryMapOrders.reduce((totals, order) => {
      const key = getCoordinateKey(order);

      totals[key] = (totals[key] || 0) + 1;

      return totals;
    }, {});
    const coordinateIndexes = {};

    deliveryMapOrders.forEach((order) => {
      const orderType = deliveryOrderTypes[order.type];
      const coordinateKey = getCoordinateKey(order);
      const coordinateIndex = coordinateIndexes[coordinateKey] || 0;
      const position = getOffsetPosition(
        order,
        coordinateIndex,
        coordinateTotals[coordinateKey],
      );

      coordinateIndexes[coordinateKey] = coordinateIndex + 1;

      const point = { lat: position[0], lng: position[1] };
      const marker = new window.google.maps.Marker({
        position: point,
        map: mapInstanceRef.current,
        title: `Order #${order.id} · ${order.area}`,
        icon: { url: orderType.icon, scaledSize: new window.google.maps.Size(34, 42) },
      });
      const infoWindow = new window.google.maps.InfoWindow({ content: `<strong>Order #${order.id}</strong><br/>${orderType.label}<br/>${order.customer}<br/>${order.area}<br/>Status: ${formatOrderStatusLabel(order.status)}${order.hasExactLocation ? "" : "<br/><em>Approximate location</em>"}` });
      marker.addListener("click", () => {
        const nextStatus = getNextDashboardStatus(order.status);
        if (!nextStatus) {
          infoWindow.open({ map: mapInstanceRef.current, anchor: marker });
          return;
        }
        setDetailView("delivery");
        setRequestedStatusAction({ orderId: order.id, status: nextStatus, requestId: Date.now() });
        setDetailOrder({ id: order.id, status: getDashboardStatusKey(order.status) || order.status });
      });
      markerRefs.current.push(marker);
      bounds.push(point);
    });

    if (bounds.length > 0) {
      const mapBounds = new window.google.maps.LatLngBounds();
      bounds.forEach((point) => mapBounds.extend(point));
      // Keep markers clear of the floating legend and activity summary.
      const compactMap = window.innerWidth < 768;
      mapInstanceRef.current.fitBounds(mapBounds, compactMap ? {
        top: 72,
        right: 30,
        bottom: 82,
        left: 30,
      } : {
        top: 92,
        right: 245,
        bottom: 88,
        left: 78,
      });
    }
  }, [deliveryMapOrders, mapReady]);

  // eslint-disable-next-line no-unused-vars
  const handleCancel = async (row) => {
    const rowLabel = isDashboardQuotationRequest(row)
      ? `quote request ${row.referenceNo}`
      : `order ${row.referenceNo}`;

    if (!await confirmAction({ title: "Cancel order?", message: `Cancel ${rowLabel}?`, confirmLabel: "Cancel order", destructive: true })) return;

    try {
      setMessage("");
      setError("");

      if (isDashboardQuotationRequest(row)) {
        await cancelQuotation(row.id);
      } else {
        await dispatch(cancelOrderCached(row.id)).unwrap();
      }

      await refreshOrderAndQuoteData();
      setMessage(`${getDashboardRowTypeLabel(row)} ${row.referenceNo} cancelled.`);
    } catch (cancelError) {
      setError(cancelError.message || "Unable to cancel order.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      setError("");
      const isKupatRequest = deleteTarget.rowType === "kupat_holim_request";
      const orderId = linkedOrderId(deleteTarget);

      if (isKupatRequest) {
        if (orderId) await dispatch(deleteOrderCached(orderId)).unwrap();
        await deleteKupatHolimRequest(deleteTarget.id);
      } else {
        await dispatch(deleteOrderCached(deleteTarget.id)).unwrap();
      }

      await refreshOrderAndQuoteData();
      setMessage(isKupatRequest
        ? orderId
          ? "Kupat Holim request and linked order deleted."
          : "Kupat Holim request deleted."
        : "Order deleted.");
      setDeleteTarget(null);
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete this record.");
    } finally {
      setDeleting(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenQuote = (order) => {
    if (!isDashboardQuotationRequest(order) || !order.actions?.addMedicine) {
      setError(
        hasQuoteBeenResponded(order)
          ? "This quotation has already been responded."
          : "Medicine can only be added while quote is requested or pending.",
      );
      return;
    }

    setError("");
    setQuoteOrder(order);
    setQuoteError("");
  };

  const handleCloseQuote = () => {
    setQuoteOrder(null);
    setQuoteError("");
  };

  const handleSendQuote = async (payload) => {
    if (!quoteOrder?.quotationId) {
      setQuoteError("This order does not have a quotation ID.");
      return;
    }

    if (!quoteOrder.actions?.addMedicine) {
      setQuoteError("This quotation has already been responded.");
      return;
    }

    try {
      setQuoteSaving(true);
      setQuoteError("");
      await dispatch(
        respondToQuotationCached({ quotationId: quoteOrder.quotationId, payload }),
      ).unwrap();
      await refreshOrderAndQuoteData().catch(() => {});
      setMessage(`Quotation #${quoteOrder.quotationId} sent successfully.`);
      handleCloseQuote();
    } catch (submitError) {
      setQuoteError(submitError.message || "Unable to send quotation.");
    } finally {
      setQuoteSaving(false);
    }
  };

  const deliveryMapPanel = (
    <div className="card rounded-4 delivery-map-card">
      <div>
        <div className="map-section delivery-map-section">
          <div className="delivery-live-map" ref={mapRef}>{mapError && <div className="delivery-map-error">{mapError}</div>}</div>
          <div className="delivery-map-overlay delivery-map-legend" aria-label="Delivery priority legend">
            {deliveryOrderNotations.map((item) => (
              <div className="delivery-map-legend-item" key={item.label}>
                <img src={item.icon} alt="" />
                <span>{item.legendLabel}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
          <div className="delivery-map-overlay delivery-map-activity">
            <span className="delivery-map-activity-icon" aria-hidden="true">⌖</span>
            <div>
              <strong>{deliveryMapOrders.length} active deliveries</strong>
              <span>Live work queue</span>
            </div>
          </div>
          <div className="delivery-map-attribution">Google Maps</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="main-wrapper dashboard-shell">
      <div className="page-wrapper">
        <div className="page-content">
          {/* <!--dashboard stats cards section start here--> */}
          <div className="dashboard-overview-stats row g-3">
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="card p-3 rounded-4 total-user">
                <div className="d-flex mb-3 row align-items-center justify-content-between">
                  <div className="col-9">
                    <h6 className="mb-2">Total Users</h6>
                    <h3>{stats.totalUsers}</h3>
                  </div>
                  <div className="col-3">
                    <div className="dash-icons">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-users-round-icon lucide-users-round"><path d="M18 21a8 8 0 0 0-16 0"/><circle cx="10" cy="8" r="5"/><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"/></svg>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 groth-up">
                  <svg className="green-arrow lucide lucide-trending-up-icon lucide-trending-up" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/></svg>
                  Live admin user list
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="card p-3 rounded-4 total-request">
                <div className="d-flex mb-3 row align-items-center justify-content-between">
                  <div className="col-9">
                    <h6 className="mb-2">Today's Quote Requests</h6>
                    <h3>{stats.todaysQuoteRequests}</h3>
                  </div>
                  <div className="col-3">
                    <div className="dash-icons">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user-check-icon lucide-user-check"><path d="m16 11 2 2 4-4"/><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 groth-up">
                  <svg className="green-arrow lucide lucide-trending-up-icon lucide-trending-up" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/></svg>
                  Quotation source orders
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="card p-3 rounded-4 total-orders">
                <div className="d-flex mb-3 row align-items-center justify-content-between">
                  <div className="col-9">
                    <h6 className="mb-2">Total Orders</h6>
                    <h3>{stats.totalOrders}</h3>
                  </div>
                  <div className="col-3">
                    <div className="dash-icons">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-package-icon lucide-package"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><polyline points="3.29 7 12 12 20.71 7"/><path d="m7.5 4.27 9 5.15"/></svg>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 groth-up">
                  <svg className="green-arrow lucide lucide-trending-up-icon lucide-trending-up" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/></svg>
                  Product and quotation orders
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="card p-3 rounded-4 total-sales">
                <div className="d-flex mb-3 row align-items-center justify-content-between">
                  <div className="col-9">
                    <h6 className="mb-2">Total Sales</h6>
                    <h3>{stats.deliveredSales.toFixed(2)} NIS</h3>
                  </div>
                  <div className="col-3">
                    <div className="dash-icons">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-gem-icon lucide-gem"><path d="M10.5 3 8 9l4 13 4-13-2.5-6"/><path d="M17 3a2 2 0 0 1 1.6.8l3 4a2 2 0 0 1 .013 2.382l-7.99 10.986a2 2 0 0 1-3.247 0l-7.99-10.986A2 2 0 0 1 2.4 7.8l2.998-3.997A2 2 0 0 1 7 3z"/><path d="M2 9h20"/></svg>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 groth-up">
                  <svg className="green-arrow lucide lucide-trending-up-icon lucide-trending-up" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/></svg>
                  Delivered orders
                </div>
              </div>
            </div>
          </div>
          {/* <!--dashboard stats cards section end here--> */}

          {/* <!--Quote requests / orders section start here--> */}
          <div className="dashboard-workspace-row row mt-4">
            <div className="col-xl-10">
              <div className="card rounded-4">
                <div className="dashboard-order-board">
                  {dashboardLoading && <div className="dashboard-board-loading"><Skeleton width="90px" height="1rem" /></div>}

                  <div className="responisve-table">
                    <table width="100%" border="0" cellSpacing="0" cellPadding="0">
                      <thead>
                        <tr>
                          <th width="17%"><span className="dashboard-date-heading">Date &amp; time <button type="button" className={`dashboard-today-filter ${todayOnly ? "is-active" : ""}`} onClick={() => { setTodayOnly((current) => !current); setDashboardPage(1); }} aria-pressed={todayOnly}>Today</button></span></th>
                          <th width="19%">Patient</th>
                          <th width="14%">ID number</th>
                          <th width="25%">Address</th>
                          <th width="16%">Status</th>
                          <th className="dashboard-actions-column">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardLoading && (
                          <>
                            {[1, 2, 3].map((item) => (
                              <tr key={item}>
                                <td colSpan="6" className="py-4">
                                  <Skeleton width="100%" height="1.5rem" />
                                </td>
                              </tr>
                            ))}
                          </>
                        )}

                        {!dashboardLoading && paginatedDashboardRows.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              {todayOnly ? "No active orders are scheduled for today." : dashboardSearch ? "No active orders match your search." : "No quote requests or orders found."}
                            </td>
                          </tr>
                        )}

                        {!dashboardLoading &&
                          paginatedDashboardRows.map((order) => (
                            <tr key={order.rowId}>
                              <td>
                                <strong>{formatOrderDate(order)}</strong>
                                <p className="small text-muted mb-0">{formatOrderTime(order)} · {order.referenceNo}</p>
                              </td>
                              <td>{getDashboardClientName(order)}</td>
                              <td><span className="dashboard-reference-id">{order.idNumber || "—"}</span></td>
                              <td className="dashboard-address-cell"><DashboardAddressCell order={order} /></td>
                              <td>
                                <div className="status-section">
                                  <DashboardStatusControl
                                    order={order}
                                    onAdvance={(targetOrder, nextStatus) => {
                                      setDetailView("delivery");
                                      setRequestedStatusAction({ orderId: targetOrder.id, status: nextStatus, requestId: Date.now() });
                                      setDetailOrder(targetOrder);
                                    }}
                                  />
                                  {(getDashboardActivityTime(order) || getDashboardActivityActor(order) || getOrderDashboardStatus(order) === "shipped") && <div className={`dashboard-status-activity${getOrderDashboardStatus(order) === "shipped" ? " dashboard-status-activity-driver" : ""}`}><span>{getDashboardActivityTime(order)}</span>{getOrderDashboardStatus(order) === "shipped" ? (() => {
                                    const assignedDriverId = String(getAssignedDriverId(order));
                                    const assignedDriver = drivers.find((driver) => String(driver.id || driver._id) === assignedDriverId);
                                    const savingDriver = driverSavingOrderId === String(order.id);
                                    return <div className="dashboard-driver-slot"><select className="dashboard-driver-select" value={assignedDriverId} disabled={savingDriver} onChange={(event) => reassignDriver(order, event.target.value)} aria-label={`Assigned driver: ${assignedDriver ? getDriverLabel(assignedDriver) : "Unassigned"}`}>
                                      <option value="">Select driver</option>
                                      {assignedDriverId && !assignedDriver && <option value={assignedDriverId}>{String(order.driverName || order.driver?.name || "Assigned driver").trim().split(/\s+/)[0]}</option>}
                                      {drivers.map((driver) => <option key={driver.id || driver._id} value={driver.id || driver._id}>{getDriverFirstWord(driver)}</option>)}
                                    </select>{savingDriver && <small className="dashboard-driver-saving">Updating driver…</small>}{driverAssignmentError.orderId === String(order.id) && !savingDriver && <small className="dashboard-driver-error">{driverAssignmentError.message}</small>}</div>;
                                  })() : <span className="dashboard-status-actor">{getDashboardActivityActor(order)}</span>}</div>}
                                </div>
                              </td>
                              <td className="dashboard-row-actions dashboard-actions-column">
                                <div className="dashboard-table-actions">
                                  {order.rowType === "order" ? (
                                    <div className="dashboard-kupat-actions">
                                      <button type="button" className="dashboard-table-action is-view" onClick={() => { setDetailView("delivery"); setDetailOrder(order); }} aria-label="View order" title="View order"><i className="bi bi-eye"></i></button>
                                      {["to_be_prepared", "prepared", "shipped"].includes(getOrderDashboardStatus(order)) && <>
                                        <button type="button" className="dashboard-table-action is-delivery-label" onClick={() => setDeliveryLabelOrder(order)} aria-label="Delivery label" title="Delivery label"><i className="bi bi-truck"></i></button>
                                      </>}
                                      {["to_be_prepared", "prepared", "shipped", "delivered"].includes(getOrderDashboardStatus(order)) && <button type="button" className="dashboard-table-action is-health-questionnaire" onClick={() => { setDetailView("questionnaire"); setDetailOrder(order); }} aria-label="Health questionnaire" title="Health questionnaire"><i className="bi bi-file-earmark-text"></i></button>}
                                      {getOrderDashboardStatus(order) !== "delivered" && <button type="button" className="dashboard-table-action is-edit-order" onClick={() => setRescheduleOrder(order)} aria-label="Reschedule delivery" title="Reschedule delivery"><i className="bi bi-pencil"></i></button>}
                                    </div>
                                  ) : order.rowType === "kupat_holim_request" && ["approved_prescription_found", "to_prepare", "to_be_invoiced"].includes(getNormalizedStatus(order.statusValue || order.status)) ? (
                                    <div className="dashboard-kupat-actions">
                                      {getNormalizedStatus(order.statusValue || order.status) !== "to_be_invoiced" && linkedOrderId(order) && <button type="button" className="dashboard-table-action is-health-questionnaire" onClick={() => { setDetailView("questionnaire"); setDetailOrder(order); }} aria-label="Health questionnaire" title="Health questionnaire"><i className="bi bi-file-earmark-text"></i></button>}
                                      <button type="button" className="dashboard-table-action is-billing" onClick={() => navigate(`/billing?request=${encodeURIComponent(order.id)}`)} aria-label="Billing" title="Billing"><i className="bi bi-calculator"></i></button>
                                    </div>
                                  ) : order.rowType === "kupat_holim_request" && order.status === "pending" && order.actions?.review ? (
                                    <div className="dashboard-kupat-actions">
                                      <button type="button" className="dashboard-table-action is-review" onClick={() => openKupatReviewFromDashboard(order)} aria-label="Review Kupat Holim request" title="Review Kupat Holim request"><span>GO</span><i className="bi bi-chevron-right"></i></button>
                                      <button type="button" className="dashboard-table-action is-reminder" onClick={() => reviewKupatRequestFromDashboard(order, "failed")} disabled={kupatReviewSavingId === String(order.id)} aria-label="Mark Kupat Holim request as failed" title="Mark as failed"><i className="bi bi-x-lg"></i></button>
                                      <button type="button" className="dashboard-table-action is-no-prescription" onClick={() => reviewKupatRequestFromDashboard(order, "approved_no_prescription")} disabled={kupatReviewSavingId === String(order.id)} aria-label="Approve Kupat Holim request with no prescription" title="Approve with no prescription"><i className="bi bi-x-lg"></i></button>
                                    </div>
                                  ) : order.actions?.view ? (
                                  <button type="button" className="dashboard-table-action is-view" onClick={() => { setDetailView("delivery"); setDetailOrder(order); }} aria-label="View order" title="View order"><i className="bi bi-eye"></i></button>
                                  ) : (
                                    <span className="text-muted small">No action</span>
                                  )}
                                  <button type="button" className="dashboard-table-action is-delete" onClick={() => setDeleteTarget(order)} aria-label="Delete" title="Delete"><i className="bi bi-trash3"></i></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {!dashboardLoading && dashboardList.total > 0 && (
                    <div className="dashboard-compact-pagination">
                      <p className="small text-muted mb-0">
                        Showing {firstVisibleRow}-{lastVisibleRow} of {dashboardList.total}
                      </p>

                      {dashboardTotalPages > 1 && <nav aria-label="Work queue pagination" className="dashboard-pager">
                        <button type="button" onClick={() => setDashboardPage((currentPage) => Math.max(1, currentPage - 1))} disabled={currentDashboardPage === 1} aria-label="Previous page"><i className="bi bi-chevron-left"></i></button>
                        <span>{currentDashboardPage} <small>/ {dashboardTotalPages}</small></span>
                        <button type="button" onClick={() => setDashboardPage((currentPage) => Math.min(dashboardTotalPages, currentPage + 1))} disabled={currentDashboardPage === dashboardTotalPages} aria-label="Next page"><i className="bi bi-chevron-right"></i></button>
                      </nav>
                      }
                    </div>
                  )}
                </div>
              </div>
              <div className="dashboard-map-panel">{deliveryMapPanel}</div>
            </div>
            <div className="col-xl-2 mt-4 mt-xl-0">
              <aside className="dashboard-action-rail">
                <div className="dashboard-action-card dashboard-action-card-primary dashboard-action-card-static">
                  <span className="dashboard-action-icon">GO</span>
                  <span>
                    <strong>Kupat Holim verification</strong>
                    <small>Review and finalise patient linking requests from the dashboard.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-warning">
                  <span className="dashboard-action-icon"><i className="bi bi-x-lg"></i></span>
                  <span>
                    <strong>Activation reminder</strong>
                    <small>Remind the patient to activate the Kupat Holim option.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-static dashboard-action-card-no-prescription">
                  <span className="dashboard-action-icon"><i className="bi bi-x-lg"></i></span>
                  <span>
                    <strong>When no valid prescription</strong>
                    <small>Use when a valid prescription cannot be accessed for the order.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-static dashboard-action-card-billing">
                  <span className="dashboard-action-icon"><i className="bi bi-calculator"></i></span>
                  <span>
                    <strong>Billing</strong>
                    <small>Open the billing screen for the selected order.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-static">
                  <span className="dashboard-action-icon"><i className="bi bi-eye"></i></span>
                  <span>
                    <strong>View order</strong>
                    <small>Open and print the delivery note.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-static">
                  <span className="dashboard-action-icon"><i className="bi bi-truck"></i></span>
                  <span>
                    <strong>Delivery label</strong>
                    <small>Create and print the delivery label.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-static">
                  <span className="dashboard-action-icon"><i className="bi bi-pencil"></i></span>
                  <span>
                    <strong>Edit order</strong>
                    <small>Change medicines, delivery address, or order details.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-static">
                  <span className="dashboard-action-icon"><i className="bi bi-file-earmark-text"></i></span>
                  <span>
                    <strong>Health questionnaire</strong>
                    <small>Open the archived health questionnaire.</small>
                  </span>
                </div>
                <div className="dashboard-action-card dashboard-action-card-danger">
                  <span className="dashboard-action-icon"><i className="bi bi-trash3"></i></span>
                  <span>
                    <strong>Delete order</strong>
                    <small>Delete the selected order after confirmation.</small>
                  </span>
                </div>
                <p className="dashboard-action-note">At the end of each day, move delivered orders to Archives.</p>
              </aside>
            </div>
          </div>
          {/* <!--Quote requests / orders section end here--> */}

        </div>
      </div>

      <QuoteModal
        show={Boolean(quoteOrder)}
        order={quoteOrder}
        saving={quoteSaving}
        error={quoteError}
        onClose={handleCloseQuote}
        onSubmit={handleSendQuote}
      />
      <OrderDetailModal
        show={Boolean(detailOrder)}
        item={detailOrder}
        view={detailView}
        loadDetail={detailView === "questionnaire" ? getKupatHolimRequest : undefined}
        onClose={() => { setDetailOrder(null); setDetailView("delivery"); setRequestedStatusAction(null); }}
        onOrderUpdated={refreshOrderAndQuoteData}
        requestedStatusAction={requestedStatusAction?.orderId === detailOrder?.id ? requestedStatusAction : null}
        hideOrderDetail={requestedStatusAction?.orderId === detailOrder?.id}
        onWorkflowActionComplete={() => { setDetailOrder(null); setDetailView("delivery"); setRequestedStatusAction(null); }}
      />
      <KupatHolimReviewModal
        show={kupatReviewLoading || Boolean(kupatReviewRequest) || Boolean(kupatReviewError)}
        loading={kupatReviewLoading}
        request={kupatReviewRequest}
        error={kupatReviewError}
        saving={kupatReviewSaving}
        onClose={closeKupatReviewFromDashboard}
        onSubmit={submitKupatReviewFromDashboard}
      />
      <DeliveryLabelModal show={Boolean(deliveryLabelOrder)} item={deliveryLabelOrder} onClose={() => setDeliveryLabelOrder(null)} />
      {rescheduleOrder && <RescheduleDeliveryModal order={rescheduleOrder} onClose={() => setRescheduleOrder(null)} onSaved={refreshOrderAndQuoteData} />}
      {deleteTarget && <>
        <div className="modal show" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content dashboard-delete-modal">
              <div className="modal-body p-4">
                <div className="dashboard-delete-icon"><i className="bi bi-trash3" /></div>
                <h5>Delete {deleteTarget.rowType === "kupat_holim_request" ? "request" : "order"}?</h5>
                <p>{deleteTarget.rowType === "kupat_holim_request" && linkedOrderId(deleteTarget)
                  ? "This will permanently delete the Kupat Holim request and its linked order."
                  : "This action permanently deletes this record and cannot be undone."}</p>
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button type="button" className="btn btn-light" disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button type="button" className="btn btn-danger" disabled={deleting} onClick={confirmDelete}>{deleting ? "Deleting…" : "Delete"}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" onClick={deleting ? undefined : () => setDeleteTarget(null)} />
      </>}
    </div>
  );
};

export default Dashboard;
